import * as React from 'react';
import s from '../InvoicePay.scss';
import { Text } from '@wix/design-system';
import { TFunction, withTranslation } from 'react-i18next';
import { ICashierPaymentsProps } from '@wix/cashier-payments-widget';
import { CashierPaymentsWidgetLazy } from '@wix/cashier-payments-widget/lazy';
import { ErrorBoundary } from '../../../api/error-reporter/error-reporter';
import { CashierEmptyState } from '../CashierEmptyState/CashierEmptyState';
import { getPaymentAmount } from '../get-payment-amount';
import { getCurrencyByKey } from '@wix/locale-dataset-javascript';
import Decimal from 'decimal.js';
import { DepositType } from '@wix/ambassador-wix-quotes-web/types';
import { CustomizedPayButton } from '../CustomizePay/CustomizePay';
import { SimpleInvoiceSettings } from '../../../routes/handlers/invoiceSettings';

export type InvoicePayData = {
  metaData?: {
    sourceProperties?: {
      billingCycle: string;
      numberOfBillingCycles: string;
      frequency: string;
    };
  };
  customer: {
    email?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
  };
  quoteNumber: string;
  deposit?: {
    type?: DepositType;
    flatAmount?: number;
  };
  balance: number;
  paid?: number;
  subtotal: number;
  discountRate: number;
  discountAmount: number;
  taxAmount: number;
  taxes: {
    name: string;
    rate: number;
    taxable: number;
    amount: number;
    code: string;
  }[];
  currency: string;
  total: number;
  locale: string;
};

export interface InvoicePayRecurringProps {
  t: TFunction;
  invoice: InvoicePayData;
  currencyFormatter: (money: Decimal.Value) => string;
  cashierPaymentsProps: ICashierPaymentsProps;
  businessEmail: string;
  customizePayMethod?: () => Promise<any>;
  backUrl?: string;
  errorUrl?: string;
  invoiceSettings: SimpleInvoiceSettings;
}

export interface DepositDueProps {
  t: TFunction;
  deposit: { type?: DepositType; flatAmount?: number };
  currency: string;
  total: number;
  currencyFormatter: (value: Decimal.Value) => string;
}

export const DepositDue: React.FunctionComponent<DepositDueProps> = ({
  deposit,
  currency,
  total,
  currencyFormatter,
  t,
}) => {
  const isPercentageTypeDeposit = deposit?.type === DepositType.PERCENTAGE;

  const convertFlatDepositToPercentages = () => {
    const { fraction } = getCurrencyByKey(currency);
    const depositAmount = new Decimal(deposit?.flatAmount);
    return depositAmount.mul(100).dividedBy(total).toFixed(fraction).toString();
  };

  return (
    <div>
      <div className={s.pair}>
        <div className={s.label} data-hook="deposit-label">
          {isPercentageTypeDeposit && currency
            ? t('quoteview.uou_percentage_deposit', {
                percentage: convertFlatDepositToPercentages(),
              })
            : t('quoteview.uou_deposit')}
        </div>
        <div className={s.value} data-hook="deposit">
          {currencyFormatter(deposit?.flatAmount)}
        </div>
      </div>
    </div>
  );
};

export const InvoicePayMainSectionComp: React.FunctionComponent<
  InvoicePayRecurringProps
> = ({
  t,
  invoice,
  businessEmail,
  cashierPaymentsProps,
  currencyFormatter,
  customizePayMethod,
  backUrl,
  errorUrl,
  invoiceSettings,
}) => {
  const hasDepositBalance = () => {
    if (
      !!invoice.deposit?.flatAmount &&
      invoice.paid !== null &&
      invoice.paid !== undefined
    ) {
      return Number(invoice.deposit.flatAmount) > invoice.paid;
    }
    return false;
  };
  const balanceAmount = getPaymentAmount({
    balance: invoice.balance,
    depositDue: invoice.deposit?.flatAmount,
    totalPayments: invoice.paid,
  });

  const hasCustomPayMethod = !!customizePayMethod;

  return (
    <section className={s.sectionMain}>
      <div className={s.paymentContainer} data-hook="payment-container">
        <ErrorBoundary fallback={<CashierEmptyState />}>
          {cashierPaymentsProps && (
            <CashierPaymentsWidgetLazy {...cashierPaymentsProps} />
          )}
          {hasCustomPayMethod && (
            <CustomizedPayButton
              customizePayMethod={customizePayMethod}
              backUrl={backUrl}
              errorUrl={errorUrl}
            />
          )}

          {cashierPaymentsProps && businessEmail && (
            <Text weight="normal" size="tiny" skin="standard" light secondary>
              <div className={s.submitContactInformation}>
                {t('invoice.recurring.pay.submit.contact-information', {
                  email: businessEmail,
                })}
              </div>
            </Text>
          )}
          <div
            className={`${s.securityIcon} ${s.hideMobile}`}
            data-hook="security-icon"
          />
        </ErrorBoundary>
      </div>
      <div className={`${s.summaryContainer} ${s.hideMobile}`}>
        <div className={s.summary} data-hook="payment-summary">
          {t('quoteview.uou_payment_summary')}
        </div>
        <div className={`${s.pair} ${s.top} ${s.separator}`}>
          <div className={s.label} data-hook="invoice-number-label">
            {t('quoteview.uou_invoice_number')}
          </div>
          <div className={s.value} data-hook="invoice-number">
            {invoice.quoteNumber}
          </div>
        </div>

        {invoiceSettings.enabled ? (
          <>
            <div className={s.pair}>
              <div className={s.label} data-hook="invoice-net-total-label">
                {t('invoice-view:quote.form.net-total')}
              </div>
              <div className={s.value} data-hook="invoice-net-total">
                {currencyFormatter(invoice.subtotal)}
              </div>
            </div>
            {invoice?.discountRate && invoice?.discountAmount ? (
              <div className={s.pair}>
                <div className={s.label} data-hook="invoice-discount-label">
                  {t('invoice-view:price-quote.view.totals.discount', {
                    rate: invoice.discountRate,
                  })}
                </div>
                <div className={s.value} data-hook="invoice-discount">
                  {currencyFormatter(invoice.discountAmount)}
                </div>
              </div>
            ) : null}
            {invoiceSettings.showTaxBreakdown ? (
              (invoice?.taxes ?? []).map((tax, index) => (
                <div className={s.pair}>
                  <div
                    className={s.label}
                    data-hook={`invoice-tax-item-label-${index}`}
                  >
                    {t('invoice-view:price-quote.view.totals.tax-rate', {
                      name: tax.name,
                      rate: tax?.rate ?? 0,
                    })}
                  </div>
                  <div
                    className={s.value}
                    data-hook={`invoice-tax-item-${index}`}
                  >
                    {currencyFormatter(tax?.amount)}
                  </div>
                </div>
              ))
            ) : invoice?.taxAmount ? (
              <div className={s.pair}>
                <div className={s.label} data-hook="invoice-tax-total-label">
                  {t('invoice-view:price-quote.view.totals.taxes')}
                </div>
                <div className={s.value} data-hook="invoice-tax-total">
                  {currencyFormatter(invoice?.taxAmount)}
                </div>
              </div>
            ) : null}
          </>
        ) : null}

        <div className={`${s.pair} ${s.extraSeparator} ${s.separator}`}>
          <div className={s.label} data-hook="total-label">
            {t('quoteview.uou_total')}
          </div>
          <div className={s.value} data-hook="total">
            {currencyFormatter(invoice.total)}
          </div>
        </div>
        <div className={s.pair}>
          <div className={s.label} data-hook="paid-label">
            {t('quoteview.uou_paid')}
          </div>
          <div className={s.value} data-hook="paid">
            {currencyFormatter(invoice.paid ? -1 * invoice.paid : 0)}
          </div>
        </div>
        {hasDepositBalance() ? (
          <DepositDue
            deposit={invoice.deposit}
            currency={invoice.currency}
            total={invoice.total}
            currencyFormatter={currencyFormatter}
            t={t}
          />
        ) : null}
        <div
          className={`${s.pair} ${s.separator} ${s.extraSeparator} ${s.balance}`}
        >
          <div className={s.label} data-hook="balance-label">
            {hasDepositBalance()
              ? t('quoteview.uou_deposit_balance')
              : t('quoteview.uou_balance')}
          </div>
          <div className={s.value} data-hook="balance">
            {currencyFormatter(balanceAmount)}
          </div>
        </div>
        <div className={`${s.pair} ${s.separator}`} />
      </div>
    </section>
  );
};

export const InvoicePayMainSection = withTranslation()(
  InvoicePayMainSectionComp,
);
